import React, { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Sparkles, Loader2, XCircle } from 'lucide-react';
import { Thought, ToolResultThought } from './types';
import { ThoughtRenderer } from './ThoughtRenderer';
import { ThoughtMarkdown } from './ThoughtMarkdown';
import { cn } from '@/lib/utils';
import { getToolIcon, getToolDisplayName, getToolConfig } from './toolCategories';
import { ICON_SIZES } from "@/lib/icon-sizes";

interface ThoughtItemProps {
  thought: Thought;
  isLast?: boolean;
  onDocumentClick?: (documentId: string, url?: string | null, name?: string) => void;
  onTemplateClick?: (templateId: string) => void;
}

export const ThoughtItem: React.FC<ThoughtItemProps> = ({
  thought,
  isLast = false,
  onDocumentClick,
  onTemplateClick
}) => {
  const [isOpen, setIsOpen] = useState(false);
  
  // Determinar tipo y estado
  const isToolResult = thought.type === 'tool_result';
  const isToolCall = thought.type === 'tool_call';
  const toolResult = isToolResult ? (thought as ToolResultThought) : null;
  const toolName = isToolCall
    ? (thought.tool_name || '')
    : isToolResult
      ? (toolResult?.tool_name || toolResult?.metadata?.tool_name || (thought as any).tool_name || '')
      : '';
  
  // Determinar estado de tool call
  const toolCallStatus = isToolCall 
    ? (thought as any).output ? 'completed' : 'running'
    : isToolResult 
      ? (toolResult?.success === false ? 'error' : 'completed')
      : null;
  
  // Ocultar tools configuradas como hidden
  if ((isToolCall || isToolResult) && toolName) {
    const config = getToolConfig(toolName);
    if (config.hidden) return null;
  }

  // Obtener icono según tipo
  const getIcon = () => {
    if (isToolCall) {
      if (toolCallStatus === 'running') {
        return <Loader2 className="h-3 w-3 text-blue-500 animate-spin" />;
      } else if (toolCallStatus === 'error') {
        return <XCircle className="h-3 w-3 text-red-500" />;
      } else {
        const { Icon, assetPath, color } = getToolIcon(toolName);
        if (assetPath) {
          return (
            <img 
              src={assetPath} 
              alt={toolName} 
              style={{ width: ICON_SIZES.sm, height: ICON_SIZES.sm }} 
              className="flex-shrink-0 object-contain" 
            />
          );
        }
        return <Icon className={`h-3 w-3 ${color}`} />;
      }
    }
    if (isToolResult) {
      if (toolResult?.success === false) {
        return <XCircle className="h-3 w-3 text-red-500" />;
      } else {
        const { Icon, assetPath, color } = getToolIcon(toolName);
        if (assetPath) {
          return (
            <img 
              src={assetPath} 
              alt={toolName} 
              style={{ width: ICON_SIZES.sm, height: ICON_SIZES.sm }} 
              className="flex-shrink-0 object-contain" 
            />
          );
        }
        return <Icon className={`h-3 w-3 ${color}`} />;
      }
    }
    return <Sparkles className="h-3 w-3 text-purple-500" />;
  };
  
  // Obtener título según tipo
  const getTitle = () => {
    if (isToolCall) {
      return getToolDisplayName(toolName || 'Tool Call');
    }
    if (isToolResult) {
      return getToolDisplayName(toolName || 'Tool Result');
    }
    return 'Reasoning';
  };
  
  // Obtener preview (primeras 100 caracteres sin ...)
  const getPreview = () => {
    const content = thought.thought || '';
    if (content.length <= 100) {
      return content;
    }
    return content.substring(0, 100).trim();
  };
  
  // Obtener resumen para tool results
  const getToolResultSummary = () => {
    if (!isToolResult || !toolResult) return null;
    
    const metadata = toolResult.metadata || {};
    const config = getToolConfig(toolName);
    
    // List Documents
    if (config.category === 'list') {
      const total = metadata.total_items || metadata.document_ids?.length || 0;
      return `${total} document${total !== 1 ? 's' : ''} available`;
    }
    
    // Get Document Content / Search
    if (config.category === 'search') {
      const docs = (toolResult as any).documents || metadata.documents;
      const total = metadata.total_found || docs?.length || (metadata.document_id ? 1 : 0);
      return `${total} document${total !== 1 ? 's' : ''} retrieved`;
    }
    
    // Document Edit tools
    if (config.category === 'edit') {
      const editContent = toolResult.content || toolResult.toolResult?.content || thought.thought || '';
      if (editContent && editContent.length > 0) {
        return editContent.length > 50 ? editContent.substring(0, 50).trim() + '...' : editContent;
      }
      return 'Document edited';
    }

    // Web Search tools
    if (config.category === 'web' || toolName.includes('web')) {
       // Look for the direct answer section
       const content = toolResult.content || toolResult.toolResult?.content || thought.thought || '';
       const marker = '💡 **Respuesta directa:**';
       const markerIndex = content.indexOf(marker);
       
       if (markerIndex !== -1) {
           const answerText = content.substring(markerIndex + marker.length).trim();
           if (answerText.length > 0) {
              return answerText.substring(0, 50) + (answerText.length > 50 ? '...' : '');
           }
       }
       
       return 'Web search completed';
    }
    
    // Default: usar content si está disponible
    const content = toolResult.content || toolResult.toolResult?.content || thought.thought || '';
    if (content && content.length > 0) {
      // Truncar a 50 caracteres para el resumen
      return content.length > 50 ? content.substring(0, 50).trim() + '...' : content;
    }
    
    return 'Completed';
  };
  
  const preview = getPreview();
  const toolResultSummary = getToolResultSummary();
  const hasMoreContent = (thought.thought || '').length > 100;
  
  // Para tool results, siempre mostrar el componente completo (no preview)
  const shouldShowPreview = !isToolResult;
  
  return (
    <div className={cn("mb-2 w-full min-w-0 @container", isLast && "mb-0")}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="flex items-start gap-2">
          {/* Icono */}
          <div className="mt-0.5 flex-shrink-0">
            {getIcon()}
          </div>
          
          {/* Contenido */}
          <div className="flex-1 min-w-0 overflow-hidden">
            {/* Header clickeable con título y preview/resumen en la misma línea */}
            <CollapsibleTrigger asChild>
              <div className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity group w-full overflow-hidden">
                <span className={cn(
                  "text-[11px] font-medium text-gray-600 truncate @[200px]:flex-shrink-0 @[200px]:truncate-none"
                )}>
                  {getTitle()}
                </span>
                {(shouldShowPreview && preview) || (!shouldShowPreview && toolResultSummary) ? (
                  <>
                    {/* Separador y preview: ocultos en contenedores < 200px */}
                    <span className="text-[11px] flex-shrink-0 text-gray-400 hidden @[200px]:inline">·</span>
                    <span className={cn(
                      "text-[11px] leading-relaxed truncate flex-1 min-w-0 text-gray-500 hidden @[200px]:inline"
                    )}>
                      {shouldShowPreview ? preview : toolResultSummary}
                    </span>
                  </>
                ) : null}
              </div>
            </CollapsibleTrigger>
            
            {/* Contenido expandido para reasoning */}
            {shouldShowPreview && hasMoreContent && (
              <CollapsibleContent>
                <div className="mt-1.5 pt-1.5">
                  <ThoughtMarkdown
                    content={thought.thought}
                    fontSize="text-[11px]"
                    textColor="text-muted-foreground"
                    onViewDocument={onDocumentClick ? (url, name) => {
                      // Convertir onDocumentClick a onViewDocument
                      // onDocumentClick espera (documentId, url, name) pero no tenemos el ID aquí
                      // Solo pasamos url y name
                      if (url) {
                        onDocumentClick('', url, name);
                      }
                    } : undefined}
                  />
                </div>
              </CollapsibleContent>
            )}
            
            {/* Para tool results, mostrar el componente completo expandido */}
            {!shouldShowPreview && (
              <CollapsibleContent>
                <div className="mt-1.5">
                  <ThoughtRenderer
                    thought={thought}
                    onDocumentClick={onDocumentClick}
                    onTemplateClick={onTemplateClick}
                  />
                </div>
              </CollapsibleContent>
            )}
          </div>
        </div>
      </Collapsible>
    </div>
  );
};

